import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getPesticideList, getRegionIndexSendCount, getRegionDateSendCount } from "../context/api_alimtalk";

// ── 공통 스타일 ──────────────────────────────────────────────
const PanelTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 5px 7px;
    border: 1px solid #ddd;
    text-align: center;
    white-space: nowrap;
  }
  th {
    background: #f0f0f0;
    font-weight: bold;
  }
  tr:nth-child(even) td { background: #fafafa; }
`;

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  padding: 8px 10px;
  background: #eef3fb;
  border-bottom: 2px solid #4a90d9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const SectionLabel = styled.span`
  font-size: 11px;
  color: #4a90d9;
  border: 1px solid #4a90d9;
  border-radius: 3px;
  padding: 1px 5px;
  margin-right: 4px;
`;

const IndexBadge = styled.span`
  display: inline-block;
  background: #4a90d9;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  padding: 1px 7px;
  border-radius: 10px;
  margin-right: 4px;
`;

const TypeBadge = styled.span`
  display: inline-block;
  background: ${({ type }) => type === "eco" ? "#5a9e6f" : "#4a90d9"};
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  padding: 1px 6px;
  border-radius: 8px;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: 0 10px 8px;
`;

const EmptyText = styled.p`
  color: #aaa;
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1 1 0;
  min-height: 0;
  border-bottom: ${({ noBorder }) => noBorder ? "none" : "3px solid #c8d8ee"};
`;

const ClickableRow = styled.tr`
  &:hover td { background: #e8f4fd !important; }
`;

// ── 헬퍼 ─────────────────────────────────────────────────────
const groupByIndex = (data) =>
  data.reduce((acc, item) => {
    const key = item.index ?? "-";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

const TYPE_LABEL = { eco: "친환경", general: "일반", 'export': '수출'};

const groupByTypeIndex = (data) => {
  const result = {};
  data.forEach((item) => {
    const t = item.type ?? "-";
    const i = item.index ?? "-";
    if (!result[t]) result[t] = {};
    if (!result[t][i]) result[t][i] = [];
    result[t][i].push(item);
  });
  return result;
};

const CollapseToggle = styled.span`
  cursor: pointer;
  font-size: 11px;
  color: #888;
  margin-left: 6px;
  user-select: none;
`;

const GroupHeader = styled.div`
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 4px;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover { opacity: 0.75; }
`;

// ── 컴포넌트 ─────────────────────────────────────────────────
const AlimtalkLeftSideComponent = ({
  setLoading,
  setError,
  targetCrop,
  targetYear,
  targetRegion,
  selectedSpot,
  setSelectedSpot,
}) => {
  const [pesticideData, setPesticideData] = useState([]);
  const [indexSendData, setIndexSendData] = useState([]);
  const [dateSendData, setDateSendData] = useState([]);
  const [collapsedPesticide, setCollapsedPesticide] = useState(new Set());
  const [collapsedIndex, setCollapsedIndex] = useState(new Set());
  const [collapsedDate, setCollapsedDate] = useState(new Set());

  const prevCropForPesticide = React.useRef(null);
  const prevYearForPesticide = React.useRef(null);
  const prevCropForSend = React.useRef(null);

  const toggle = (setter, key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // 약제: 시군 선택 시만 조회
  useEffect(() => {
    if (!targetCrop || !targetYear) return;
    if (!targetRegion || targetRegion.region === "all") {
      setPesticideData([]);
      return;
    }
    const isRegionOnlyChange =
      prevCropForPesticide.current === targetCrop &&
      prevYearForPesticide.current === targetYear;
    prevCropForPesticide.current = targetCrop;
    prevYearForPesticide.current = targetYear;
    getPesticideList(
      (data) => setPesticideData(data),
      isRegionOnlyChange ? () => {} : (val) => setLoading(val),
      (e) => setError(e),
      targetCrop,
      targetYear,
      targetRegion
    );
  }, [targetCrop, targetYear, targetRegion]);

  // 차수별·일자별 발송: 작목·시군 변경 시 재조회
  useEffect(() => {
    if (!targetCrop) return;
    const isRegionOnlyChange = prevCropForSend.current === targetCrop;
    prevCropForSend.current = targetCrop;
    const loadingFn = isRegionOnlyChange ? () => {} : (val) => setLoading(val);
    getRegionIndexSendCount(
      (data) => setIndexSendData(data),
      loadingFn,
      (e) => setError(e),
      targetCrop,
      targetRegion
    );
    getRegionDateSendCount(
      (data) => setDateSendData(data),
      loadingFn,
      (e) => setError(e),
      targetCrop,
      targetRegion
    );
  }, [targetCrop, targetRegion]);

  const pesticideGrouped = groupByIndex(pesticideData);
  const regionLabel = targetRegion?.regionName ?? "전체";

  const indexGrouped = groupByTypeIndex(indexSendData);
  const dateGrouped = groupByTypeIndex(dateSendData);

  const TYPE_ORDER = ["general", "eco", "export"];
  const allTypes = Array.from(
    new Set([...Object.keys(indexGrouped), ...Object.keys(dateGrouped)])
  ).sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a), bi = TYPE_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });


  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* ── 1. 약제 ── */}
      <Panel>
        <PanelTitle>
          <span><SectionLabel>약제</SectionLabel>{targetCrop?.titleKo ?? "-"} / {targetYear ?? "-"} / {regionLabel}</span>
        </PanelTitle>
        <ScrollArea>
          {pesticideData.length === 0 ? (
            <EmptyText>시군을 선택하면 약제 정보가 표시됩니다.</EmptyText>
          ) : (
            Object.entries(pesticideGrouped)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([index, items]) => {
                const collapsed = collapsedPesticide.has(index);
                return (
                  <div key={index} style={{ marginTop: 8 }}>
                    <GroupHeader onClick={() => toggle(setCollapsedPesticide, index)}>
                      <IndexBadge>{index}차</IndexBadge>
                      {items[0]?.pesticideClass ?? ""}
                      <CollapseToggle>{collapsed ? "▶" : "▼"}</CollapseToggle>
                    </GroupHeader>
                    {!collapsed && (
                      <PanelTable>
                        <thead>
                          <tr>
                            <th>지역</th>
                            <th>구분</th>
                            <th>약제명</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.regionName}</td>
                              <td>{item.type}</td>
                              <td>{item.pesticide}</td>
                            </tr>
                          ))}
                        </tbody>
                      </PanelTable>
                    )}
                  </div>
                );
              })
          )}
        </ScrollArea>
      </Panel>

      {/* ── 2. 차수별 농가수 대비 발송률 ── */}
      <Panel>
        <PanelTitle>
          <span><SectionLabel>차수별</SectionLabel>{targetCrop?.titleKo ?? "-"} / {regionLabel} / 차수별 발송률</span>
        </PanelTitle>
        <ScrollArea>
          {indexSendData.length === 0 ? (
            <EmptyText>데이터가 없습니다.</EmptyText>
          ) : (
            allTypes.map((type) => {
              const byIndex = indexGrouped[type] ?? {};
              const collapsed = collapsedIndex.has(type);
              return (
                <div key={type} style={{ marginTop: 8 }}>
                  <GroupHeader onClick={() => toggle(setCollapsedIndex, type)}>
                    <TypeBadge type={type}>{TYPE_LABEL[type] ?? type}</TypeBadge>
                    <CollapseToggle>{collapsed ? "▶" : "▼"}</CollapseToggle>
                  </GroupHeader>
                  {!collapsed && <PanelTable>
                    <thead>
                      <tr>
                        <th>차수</th>
                        <th>전체농가수(명)</th>
                        <th>누적발송건수(건)</th>
                        <th>발송률(%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(byIndex)
                        .sort(([a], [b]) => Number(a) - Number(b))
                        .map(([index, rows]) => {
                          const row = rows[0];
                          return (
                            <ClickableRow
                              key={index}
                            >
                              <td><IndexBadge style={{ background: "#5a9e6f" }}>{index}차</IndexBadge></td>
                              <td>{row.totFarmerCount ?? "-"}</td>
                              <td>{row.accSendCount == 0 ? "-" : row.accSendCount}</td>
                              <td>{row.accSendPercent != 0 ? `${row.accSendPercent}%` : "-"}</td>
                            </ClickableRow>
                          );
                        })}
                    </tbody>
                  </PanelTable>
                  }
                </div>
              );
            })
          )}
        </ScrollArea>
      </Panel>

      {/* ── 3. 일자별 발송 농가 수 및 발송률 (차수별로 그룹핑) ── */}
      <Panel noBorder>
        <PanelTitle>
          <span><SectionLabel>일자별</SectionLabel>{targetCrop?.titleKo ?? "-"} / {regionLabel} / 일자별 발송 농가 수</span>
        </PanelTitle>
        <ScrollArea>
          {dateSendData.length === 0 ? (
            <EmptyText>데이터가 없습니다.</EmptyText>
          ) : (
            allTypes.map((type) => {
              const dateByIndex = dateGrouped[type] ?? {};
              const indexes = Object.keys(dateByIndex).sort((a, b) => Number(a) - Number(b));
              const typeCollapsed = collapsedDate.has(type);
              return (
                <div key={type} style={{ marginTop: 8 }}>
                  <GroupHeader onClick={() => toggle(setCollapsedDate, type)}>
                    <TypeBadge type={type}>{TYPE_LABEL[type] ?? type}</TypeBadge>
                    <CollapseToggle>{typeCollapsed ? "▶" : "▼"}</CollapseToggle>
                  </GroupHeader>
                  {!typeCollapsed && indexes.map((index) => {
                    const indexKey = `${type}-${index}`;
                    const indexCollapsed = collapsedDate.has(indexKey);
                    const rows = (dateByIndex[index] ?? [])
                      .slice()
                      .sort((a, b) => (a.sendDt ?? "").localeCompare(b.sendDt ?? ""));
                    return (
                      <div key={index} style={{ marginBottom: 6 }}>
                        <GroupHeader
                          style={{ fontSize: 11, paddingLeft: 4 }}
                          onClick={() => toggle(setCollapsedDate, indexKey)}
                        >
                          <IndexBadge style={{ background: "#5a9e6f" }}>{index}차</IndexBadge>
                          <CollapseToggle>{indexCollapsed ? "▶" : "▼"}</CollapseToggle>
                        </GroupHeader>
                        {!indexCollapsed && (
                          <PanelTable>
                            <thead>
                              <tr>
                                <th>발송일</th>
                                <th>당일발송건수(건)</th>
                                <th>누적발송건수(건)</th>
                                <th>전체농가수(명)</th>
                                <th>발송률(%)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, idx) => (
                                <ClickableRow key={idx}>
                                  <td>{row.sendDt ?? "-"}</td>
                                  <td>{row.todaySendCount ?? "-"}</td>
                                  <td>{row.accSendCount ?? "-"}</td>
                                  <td>{row.totFarmerCount ?? "-"}</td>
                                  <td>{row.accSendPercent != null ? `${row.accSendPercent}%` : "-"}</td>
                                </ClickableRow>
                              ))}
                            </tbody>
                          </PanelTable>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </ScrollArea>
      </Panel>
    </div>
  );
};

export default AlimtalkLeftSideComponent;
